import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface VoiceSessionTimerProps {
  /**
   * Wall-clock anchor (ms since epoch) for the elapsed-time computation, or
   * `null` when no voice session is active yet (e.g. the parent is in
   * `connecting` state and `useConversation` has not yet recorded a start
   * timestamp). Rendering `null` short-circuits to `00:00` so the bar
   * doesn't display a 56-year overflow while the connection is being set
   * up.
   */
  startedAt: number | null;
  paused: boolean;
  pausedMs: number;
  className?: string;
}

/**
 * Formats elapsed seconds as `MM:SS`.
 *
 * Voice coaching sessions are typically 5–30 minutes; if a session ever
 * exceeds 99:59 (1h40m), the minutes field overflows to 3+ digits rather
 * than rolling over into an hours segment. This is intentional given
 * current session lifecycle constraints — revisit (and switch to
 * `HH:MM:SS`) if long-form sessions are introduced.
 */
function formatMmSs(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const m = Math.floor(safe / 60).toString().padStart(2, '0');
  const s = (safe % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function elapsedSeconds(displayNow: number, startedAt: number, pausedMs: number): number {
  return Math.floor((displayNow - startedAt - pausedMs) / 1000);
}

export function VoiceSessionTimer({
  startedAt,
  paused,
  pausedMs,
  className,
}: VoiceSessionTimerProps) {
  // When paused, freeze "now" at the moment the pause flipped on. The
  // `pausedMs` prop carries already-accumulated previous pauses; the
  // current in-progress pause is implicitly reflected by `pausedAtRef`.
  const pausedAtRef = useRef<number | null>(paused ? Date.now() : null);

  const computeNow = () => (paused && pausedAtRef.current !== null ? pausedAtRef.current : Date.now());

  const [elapsed, setElapsed] = useState<number>(() =>
    startedAt === null ? 0 : elapsedSeconds(computeNow(), startedAt, pausedMs),
  );

  useEffect(() => {
    if (startedAt === null) {
      // No anchor yet (still connecting). Render the minimum value and
      // skip the interval so we don't spin a useless tick.
      pausedAtRef.current = null;
      setElapsed(0);
      return;
    }
    if (paused) {
      pausedAtRef.current = Date.now();
      setElapsed(elapsedSeconds(pausedAtRef.current, startedAt, pausedMs));
      return;
    }
    pausedAtRef.current = null;
    setElapsed(elapsedSeconds(Date.now(), startedAt, pausedMs));
    const interval = setInterval(() => {
      setElapsed(elapsedSeconds(Date.now(), startedAt, pausedMs));
    }, 1000);
    return () => clearInterval(interval);
  }, [paused, startedAt, pausedMs]);

  return (
    <span className={cn('text-xs text-muted-foreground tabular-nums', className)}>
      {formatMmSs(elapsed)}
    </span>
  );
}
